import { Fragment, type ReactNode } from 'react';
import { MessageSquare, Server } from 'lucide-react';
import type { GatewayCommandRecord, WireguardPeer } from '../lib/api';
import { Badge } from './ui/Badge';
import { formatBytes, formatExact, formatRelative, formatUtc } from '../lib/format';
import { cn } from '../lib/utils';
import { isGoodVerdict } from '../lib/gateway-verdict';

// How one command sent to a gateway is shown, wherever it is shown: a
// gateway's own history and the fleet-wide Command History page.

const UNCONFIRMED_AFTER_MS = 2 * 60_000;

export function HistoryItem({ record: c }: { record: GatewayCommandRecord }) {
  return (
    <li className="rounded-md border border-line">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line-soft bg-panel-2 px-3 py-1.5">
        <CommandName record={c} />
        <StatusBadge record={c} />
      </div>
      <dl className="grid grid-cols-[6.5rem_1fr] gap-x-3 gap-y-1 px-3 py-2 text-xs">
        <HistoryField label="Sent">
          <span title={formatUtc(c.timestamp)}>{formatExact(c.timestamp)}</span>
          <span className="text-muted-foreground"> · {formatRelative(c.timestamp)}</span>
        </HistoryField>
        <HistoryField label="Via">
          <ViaLabel record={c} />
        </HistoryField>
        <HistoryField label="By">{c.actor}</HistoryField>
        <HistoryField label="Reason">
          {c.reason ? (
            <span className="font-medium break-words">{c.reason}</span>
          ) : (
            <span className="text-muted-foreground">None given</span>
          )}
        </HistoryField>
        <HistoryField label={c.channel === 'sms' ? 'Text sent' : 'Command'}>
          {c.channel === 'sms' ? (
            <code className="font-mono text-[11px] break-all">{c.text}</code>
          ) : (
            <details>
              <summary className="cursor-pointer text-muted-foreground">Fixed shell command (show)</summary>
              <code className="mt-1 block font-mono text-[11px] break-all">{c.text}</code>
            </details>
          )}
        </HistoryField>
        {c.deliveredAt && (
          <HistoryField label="Delivered">
            <span title={formatUtc(c.deliveredAt)}>{formatExact(c.deliveredAt)}</span>
            <span className="text-muted-foreground"> · {afterSending(c.timestamp, c.deliveredAt)} after sending</span>
          </HistoryField>
        )}
        {c.replyAt && (
          <HistoryField label={c.channel === 'sms' ? 'Replied' : 'Answered'}>
            <span title={formatUtc(c.replyAt)}>{formatExact(c.replyAt)}</span>
            <span className="text-muted-foreground"> · {afterSending(c.timestamp, c.replyAt)} after sending</span>
            {c.channel === 'sms' && (c.replyCount ?? 1) > 1 && (
              <span className="text-muted-foreground"> · {c.replyCount} texts</span>
            )}
          </HistoryField>
        )}
        {(c.summary || c.error) && (
          <HistoryField label="Result">
            {c.summary && (
              <span className={cn('font-medium', !isGoodVerdict(c.verdict) && 'text-caution')}>{c.summary}</span>
            )}
            {c.error && <span className="text-destructive">{c.error}</span>}
          </HistoryField>
        )}
      </dl>
      {c.wireguard && c.wireguard.length > 0 && (
        <div className="border-t border-line-soft px-3 py-2">
          <WireguardTunnels peers={c.wireguard} />
        </div>
      )}
      {c.reply && statusFields(c.reply) && (
        <div className="border-t border-line-soft px-3 py-2">
          <StatusFields fields={statusFields(c.reply) ?? []} />
        </div>
      )}
      {c.reply && (
        <details className="border-t border-line-soft px-3 py-2 text-xs" open={c.channel === 'sms'}>
          <summary className="cursor-pointer text-muted-foreground">
            {c.channel === 'sms' ? 'Reply text' : 'Output'}
          </summary>
          <pre className="mt-1 max-h-64 overflow-auto rounded bg-raise px-2 py-1.5 font-mono text-[11px] whitespace-pre-wrap">
            {c.reply}
          </pre>
        </details>
      )}
    </li>
  );
}

function HistoryField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="font-medium text-muted-foreground uppercase tracking-wide text-[10px] leading-5">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

function ViaLabel({ record: c }: { record: GatewayCommandRecord }) {
  return c.channel === 'sms' ? (
    <span>
      <b>SMS</b> through Hologram{c.simName ? ` to SIM ${c.simName}` : ''}
    </span>
  ) : (
    <span>
      <b>RMS</b> — remote command over Teltonika RMS
    </span>
  );
}

function afterSending(sentIso: string, replyIso: string): string {
  const s = Math.max(0, Math.round((Date.parse(replyIso) - Date.parse(sentIso)) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function CommandName({ record: c }: { record: GatewayCommandRecord }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-sm font-medium', c.effect === 'write' && 'text-caution')}>
      {c.channel === 'sms' ? <MessageSquare className="h-3.5 w-3.5" /> : <Server className="h-3.5 w-3.5" />}
      {c.label}
    </span>
  );
}

/** A row still `sending` long after the request would have finished never heard back from the provider. */
function unconfirmed(c: GatewayCommandRecord): boolean {
  return Date.now() - Date.parse(c.timestamp) > UNCONFIRMED_AFTER_MS;
}

export function StatusBadge({ record: c }: { record: GatewayCommandRecord }) {
  const [variant, label, title]: [Parameters<typeof Badge>[0]['variant'], string, string] = (() => {
    switch (c.status) {
      case 'sending':
        return unconfirmed(c)
          ? ['warning', 'Unconfirmed', 'The request ended before the provider confirmed the send. It may or may not have gone out.']
          : ['secondary', 'Sending…', ''];
      case 'pending':
        return ['secondary', 'Waiting for gateway', 'RMS accepted it; the gateway has not answered yet.'];
      case 'sent':
        return c.deliveredAt
          ? ['secondary', 'Delivered · awaiting reply', 'The SMS reached the SIM; the gateway has not answered yet.']
          : ['secondary', 'Sent · awaiting reply', 'Hologram accepted the SMS. Replies are collected for 15 minutes.'];
      case 'replied':
        return c.settledAt ? ['success', 'Replied', ''] : ['success', 'Replied · listening', 'More reply texts may still arrive.'];
      case 'done':
        return ['success', 'Done', ''];
      case 'no-reply':
        return ['warning', 'No reply', 'Nothing came back within 15 minutes. The gateway may be off, out of signal, or its SMS rule may be off.'];
      case 'failed':
        return ['destructive', 'Failed', c.error ?? ''];
    }
  })();
  return (
    <span title={title}>
      <Badge variant={variant} className="whitespace-nowrap">
        {label}
      </Badge>
    </span>
  );
}

/** One line per tunnel: its state, which link it is on, and how long since it last handshook. */
export function WireguardTunnels({ peers }: { peers: WireguardPeer[] }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="py-0.5 pr-3 font-medium">Tunnel</th>
          <th className="py-0.5 pr-3 font-medium">State</th>
          <th className="py-0.5 pr-3 font-medium">Over</th>
          <th className="py-0.5 pr-3 font-medium">Last handshake</th>
          <th className="py-0.5 pr-3 font-medium">Endpoint</th>
          <th className="py-0.5 font-medium">In / out</th>
        </tr>
      </thead>
      <tbody>
        {peers.map((p) => (
          <tr key={`${p.iface}/${p.peer}`} className="border-t border-line-soft">
            <td className="py-1 pr-3 font-mono" title={`Peer ${p.peer}`}>
              {p.iface}
            </td>
            <td className={cn('py-1 pr-3 font-medium', p.state === 'up' ? 'text-ok' : 'text-caution')}>
              {p.state === 'up' ? 'Up' : p.state === 'down' ? 'Down' : 'Never connected'}
            </td>
            <td className={cn('py-1 pr-3', p.link === 'mobile' && 'text-warn')}>
              {p.link ? `${p.link === 'mobile' ? 'Mobile' : 'Wired'}${p.routeDev ? ` (${p.routeDev})` : ''}` : '—'}
            </td>
            <td className="py-1 pr-3">{p.handshakeAgeS === null ? '—' : `${handshakeAge(p.handshakeAgeS)} ago`}</td>
            <td className="py-1 pr-3 font-mono">{p.endpoint ?? '—'}</td>
            <td className="py-1 tabular-nums">
              {p.rxBytes === null ? '—' : `${formatBytes(p.rxBytes)} / ${formatBytes(p.txBytes)}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function handshakeAge(s: number): string {
  if (s < 90) return `${s}s`;
  if (s < 90 * 60) return `${Math.round(s / 60)}m`;
  if (s < 48 * 3600) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86_400)}d`;
}

/**
 * A Teltonika status reply is "Name - value;" pairs — "Router name -
 * RUT955-ultralev; WAN IP - 10.32.2.4 ; Data Connection state - Connected; …".
 * Read as fields when it has that shape; anything else stays raw text.
 */
function statusFields(text: string): { label: string; value: string }[] | null {
  const pairs = text
    .split(';')
    .map((part) => {
      const i = part.indexOf(' - ');
      return i > 0 ? { label: part.slice(0, i).trim(), value: part.slice(i + 3).trim() } : null;
    })
    .filter((p): p is { label: string; value: string } => p !== null && p.label.length > 0 && p.label.length < 40);
  return pairs.length >= 3 ? pairs : null;
}

function StatusFields({ fields }: { fields: { label: string; value: string }[] }) {
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-0.5 text-xs">
      {fields.map((f) => (
        <Fragment key={f.label}>
          <dt className="text-muted-foreground">{f.label}</dt>
          <dd className="font-medium">{f.value || '—'}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

